// PagamentosView — unificação das antigas 3 views separadas:
//   - Assinatura (plano da plataforma)
//   - Forma de pagamento (cartões + cobranças)
//   - Controle de gastos da IA (teto mensal + BYOK)
//
// Decisão de UX (pedido do user 2026-05-20): aluno tinha que pular entre 3
// itens de sidebar pra coisas que mentalmente são "minhas finanças no app".
// Aqui virou um item só, com tabs internas. Cada tab continua sendo um
// componente independente (AssinaturaView / FormaPagamentoView / AiSpendView)
// renderizado com prop `embedded` que oculta o header redundante.
//
// Retrocompat de deeplink: App.tsx ainda aceita activeNav=assinatura |
// controlegastos | formapagamento. Os 3 caem aqui e pré-selecionam a tab
// correta via prop `initialTab`. [[project-payments-unification]]

import { useState } from "preact/hooks";
import type { ComponentChildren } from "preact";
import { BadgeDollarSign, CreditCard, Sparkles, Wallet } from "lucide-react";
import { AssinaturaView } from "./AssinaturaView";
import { FormaPagamentoView } from "./FormaPagamentoView";
import { AiSpendView } from "./AiSpendView";
import { AnimatedTabs, type AnimatedTabItem } from "@app/ui/components/animated-tabs";

export type PagamentosTab = "plano" | "cartoes" | "gastos" | "ia";

interface TabDef {
  id: PagamentosTab;
  label: string;
  shortLabel: string;
  icon: typeof BadgeDollarSign;
  description: string;
}

// Ordem decidida com user 2026-05-20: gastos primeiro (é o que abre todo
// mês pra checar consumo), depois cartões/cobranças (mais frequente que
// trocar de plano), plano por último das financeiras, e Assistente IA
// como aba de config separada das financeiras.
const TABS: TabDef[] = [
  {
    id: "gastos",
    label: "Controle de gastos da IA",
    shortLabel: "Gastos IA",
    icon: Wallet,
    description:
      "Defina um teto mensal para o uso da IA da plataforma e escolha entre nossa chave ou a sua (BYOK)."
  },
  {
    id: "cartoes",
    label: "Cartões e cobranças",
    shortLabel: "Pagamentos",
    icon: CreditCard,
    description:
      "Cadastre cartões para cobrança automática e acompanhe assinaturas ativas."
  },
  {
    id: "plano",
    label: "Plano da plataforma",
    shortLabel: "Plano",
    icon: BadgeDollarSign,
    description: "Veja seu plano atual e faça upgrade quando quiser."
  },
  {
    id: "ia",
    label: "Assistente IA",
    shortLabel: "IA",
    icon: Sparkles,
    description:
      "Configuração do tutor por questão no Modo Estudo: liga/desliga, provedor, API key, persona."
  }
];

export function PagamentosView({
  initialTab = "gastos",
  aiSettingsSlot
}: {
  initialTab?: PagamentosTab;
  /** Componente renderizado dentro da tab "Assistente IA". Recebido como
   *  prop (não importado direto) porque AiSettingsView vive dentro de
   *  App.tsx — evita import circular. */
  aiSettingsSlot?: ComponentChildren;
}) {
  const [tab, setTab] = useState<PagamentosTab>(initialTab);
  const active = TABS.find((t) => t.id === tab) ?? TABS[0];

  return (
    <div class="space-y-6">
      <header>
        <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <Wallet className="size-6 text-primary" />
          Pagamentos e consumo
        </h1>
        <p class="mt-1 text-sm text-muted-foreground">
          Tudo o que envolve consumo e dinheiro em um lugar só: controle de
          gastos, forma de pagamento, plano da plataforma e configuração do
          assistente IA.
        </p>
      </header>

      {/* Tab switcher animado — indicador desliza ao trocar de aba. */}
      <AnimatedTabs<PagamentosTab>
        ariaLabel="Seções de pagamentos"
        items={TABS.map<AnimatedTabItem<PagamentosTab>>((t) => ({
          id: t.id,
          label: t.label,
          shortLabel: t.shortLabel,
          icon: t.icon
        }))}
        value={tab}
        onChange={setTab}
      />

      <p class="text-[12px] italic text-muted-foreground">
        {active.description}
      </p>

      {/* Cada tab é um componente independente — `embedded` oculta o header
          interno pra não duplicar o nosso. Render condicional preserva o
          estado do componente quando o aluno volta na mesma tab. */}
      <div>
        {tab === "plano" && <AssinaturaView embedded />}
        {tab === "cartoes" && <FormaPagamentoView embedded />}
        {tab === "gastos" && <AiSpendView embedded />}
        {tab === "ia" && aiSettingsSlot}
      </div>
    </div>
  );
}
